import {Breadcrumb, Modal, ProgressIndicator} from '@/components/atoms';
import {PageHeader, SaveCancelFooter} from '@/components/molecules';
import {getDisplayContentLanguagesService} from '@/services/languageService';
import {useAppDispatch, useAppSelector} from '@/store';
import {setLanguages} from '@/store/slices/commonSlice';
import {resetSalesTarget} from '@/store/slices/salesTargetSlice';
import {MODAL_TYPE} from '@/utils/constants/globalStyle';
import {ROUTES} from '@/utils/constants/routesConstants';
import {TARGET_TYPES} from '@/utils/constants/saleTargetConstants';
import {
  getStepConfig,
  isCdaSuperTargetType
} from '@/utils/salesTargetStepConfig';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {COUNTRY_LANGUAGES_CODES} from '@/utils/constants/globalConstants';
import {useSalesTargets} from '@/hooks/useSalesTargets';
import {SalesTargetDisplayContentV2Layout} from '@/containers/DisplayContent/SalesTargetDisplayContentV2Layout';
import {
  getSalesTargetBackgroundMediaSlot,
  getSalesTargetMediaSlot
} from '@/utils/salesTargetDisplayContentMedia';
import {getSalesTargetDisplayContentConfig} from '@/utils/salesTargetDisplayContentConfig';
import {
  useDisplayContentForm,
  useDisplayContentPersistence,
  useDisplayContentNavigation,
  useV2DisplayContentValidation,
  useDisplayContentPublish,
  ensureDisplayContentLanguages
} from '@/hooks/useDisplayContentPage';

export const AddSalesTargetDisplayContentV2 = () => {
  const location = useLocation();
  const isEdit = location.state?.isEdit ?? false;
  const disableDraft = location.state?.disableDraft ?? false;
  const editPublishedBeforeStart =
    location.state?.editPublishedBeforeStart ?? false;
  const editMode = location.state?.editMode;
  const isEditDisplayContent = editMode === 'displayContent';
  const returnTo = location.state?.returnTo || ROUTES.SALES_TARGET_REVIEW;
  const salesTargetIdFromState = location.state?.salesTargetId;
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const salesTarget = useAppSelector((state) => state.salesTarget);
  const countryCode = useAppSelector((state) => state.countryCode)?.countryCode;
  const countryLanguages = COUNTRY_LANGUAGES_CODES[countryCode];
  const [isCancel, setIsCancel] = useState(false);
  const displayContentLanguages = useAppSelector(
    (state) => state.common.languages
  );
  const [activeLanguage, setActiveLanguage] = useState('en');
  const {handleDraft} = useSalesTargets(false);
  const displayContentMedia = salesTarget.displayContentMedia;
  const displayContentMediaOptional = salesTarget.displayContentMediaOptional;
  const mediaSlot = getSalesTargetMediaSlot(salesTarget);
  const selectedMedia = salesTarget[mediaSlot];
  const showBackgroundImage = Boolean(
    getSalesTargetDisplayContentConfig(salesTarget.targetType)
      .showBackgroundImage
  );
  const backgroundMedia = showBackgroundImage
    ? salesTarget[getSalesTargetBackgroundMediaSlot(salesTarget)]
    : null;

  const isPrerequisiteTarget =
    salesTarget.targetType === TARGET_TYPES.MonthlyTargetPrerequistite ||
    salesTarget.targetType === TARGET_TYPES.ProductTargetPrerequisite;
  const isEarlyPaymentTarget =
    salesTarget?.targetType === TARGET_TYPES.EarlyPaymentTarget;
  const isDropSizeTarget =
    salesTarget?.targetType === TARGET_TYPES.DropSizeTarget;
  const isLinkedTargetType = isEarlyPaymentTarget || isDropSizeTarget;
  const isRackChallenge =
    salesTarget?.targetType === TARGET_TYPES.RackChallenge;
  const isCdaSuperTarget = isCdaSuperTargetType(salesTarget?.targetType);
  const stepConfig = useMemo(
    () => getStepConfig(salesTarget?.targetType),
    [salesTarget?.targetType]
  );

  const {
    displayContentFormData,
    setDisplayContentFormData,
    displayPreReqContentFormData,
    setDisplayPreReqContentFormData
  } = useDisplayContentForm(salesTarget, countryCode, displayContentLanguages);

  const {persistDisplayContentToRedux} = useDisplayContentPersistence(
    dispatch,
    salesTarget,
    isPrerequisiteTarget
  );

  const {handleNextAction, handleStepClick: navigateToStep} =
    useDisplayContentNavigation(
      navigate,
      isLinkedTargetType,
      isRackChallenge,
      isEdit,
      disableDraft,
      editPublishedBeforeStart,
      salesTargetIdFromState || salesTarget?.id,
      isCdaSuperTarget
    );

  const persistCurrentDisplayContent = useCallback(
    () =>
      persistDisplayContentToRedux(
        displayContentFormData,
        displayPreReqContentFormData,
        displayContentMedia,
        displayContentMediaOptional
      ),
    [
      persistDisplayContentToRedux,
      displayContentFormData,
      displayPreReqContentFormData,
      displayContentMedia,
      displayContentMediaOptional
    ]
  );
  const handleNextActionWrapper = () =>
    handleNextAction(persistCurrentDisplayContent);
  const handleStepClickWrapper = (step) =>
    navigateToStep(persistCurrentDisplayContent, step);
  const {validateDisplayContent} = useV2DisplayContentValidation(
    salesTarget?.targetType
  );

  const handleSaveAndPublishEditDisplayContent = useDisplayContentPublish({
    salesTarget,
    salesTargetId: salesTargetIdFromState,
    isEditDisplayContent,
    returnTo,
    persistDisplayContent: persistCurrentDisplayContent,
    formData: {
      isPrerequisiteTarget,
      displayContentFormData,
      displayPreReqContentFormData,
      displayContentMedia,
      displayContentMediaOptional
    },
    dispatch,
    navigate
  });
  const handleDraftAction = async () => {
    const response = await handleDraft(
      salesTarget,
      salesTarget,
      isPrerequisiteTarget,
      'displayContent',
      {
        displayContentFormData,
        displayPreReqContentFormData
      }
    );
    if (response?.success) navigate(ROUTES.SALES_TARGETS);
  };
  const handleCancel = () => setIsCancel(!isCancel);
  const onClickPrimaryButton = () => {
    dispatch(resetSalesTarget());
    navigate(ROUTES.SALES_TARGETS);
  };
  const onClickSecondaryButton = () => setIsCancel(!isCancel);
  const getDisplayContentLanguages = () => {
    getDisplayContentLanguagesService().then((res) => {
      dispatch(setLanguages(res));
      setActiveLanguage(res[0].code);
    });
  };
  useEffect(() => {
    if (countryLanguages?.length > 0) {
      dispatch(setLanguages(countryLanguages));
      setActiveLanguage(countryLanguages[0].code);
    } else {
      getDisplayContentLanguages();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryLanguages]);
  useEffect(() => {
    if (!salesTarget.targetType) navigate(ROUTES.SALES_TARGET_ADD);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const handleTabClickPreReq = (tab) => {
    const languages =
      COUNTRY_LANGUAGES_CODES[countryCode] || displayContentLanguages || [];
    setDisplayPreReqContentFormData((prevData) =>
      tab === 'parent'
        ? ensureDisplayContentLanguages(
            prevData,
            languages,
            salesTarget?.targetType,
            true
          )
        : {
            ...prevData,
            child: ensureDisplayContentLanguages(
              prevData.child,
              languages,
              salesTarget?.targetType
            )
          }
    );
  };
  const getFormError = () => {
    if (!selectedMedia?.imageUri || !selectedMedia?.imageUrl) return true;
    if (
      showBackgroundImage &&
      (!backgroundMedia?.imageUri || !backgroundMedia?.imageUrl)
    )
      return true;
    if (isPrerequisiteTarget) {
      const parentForm = Object.fromEntries(
        Object.entries(displayPreReqContentFormData).filter(
          ([key]) => key !== 'child'
        )
      );
      return (
        validateDisplayContent(parentForm) ||
        validateDisplayContent(displayPreReqContentFormData.child)
      );
    }
    return validateDisplayContent(displayContentFormData);
  };

  return (
    <>
      <Breadcrumb
        uiSchema={{
          isDark: true,
          className: 'mb-5',
          breadcrumbs: [
            {href: ROUTES.HOME, label: ''},
            {href: ROUTES.SALES_TARGETS, label: 'Loyalty Campaigns'},
            {href: ROUTES.SALES_TARGETS, label: 'Add Sales Target'}
          ]
        }}
        id='astdc_bradcrumb'
      />
      <PageHeader
        title={
          isEditDisplayContent ? 'Edit Display Content' : 'Add Sales Target'
        }
      />
      {!isEditDisplayContent && (
        <ProgressIndicator
          currentStep={2}
          completed={false}
          orientation='horizontal'
          steps={stepConfig.steps}
          titles={stepConfig.titles}
          className='mb-6'
          onStepClick={handleStepClickWrapper}
        />
      )}
      <br />
      <SalesTargetDisplayContentV2Layout
        languages={
          countryLanguages?.length > 0
            ? countryLanguages
            : displayContentLanguages
        }
        displayPreReqContentFormData={displayPreReqContentFormData}
        setDisplayPreReqContentFormData={setDisplayPreReqContentFormData}
        handleTabClickPreReq={handleTabClickPreReq}
        displayContentFormData={displayContentFormData}
        setDisplayContentFormData={setDisplayContentFormData}
        activeLanguage={activeLanguage}
        setActiveLanguage={setActiveLanguage}
        isPrerequisiteTarget={isPrerequisiteTarget}
      />
      {isCancel ? (
        <Modal
          type={MODAL_TYPE.FLUID}
          showModal={isCancel}
          onCloseModal={handleCancel}
          title='Are you sure you want to cancel?'
          text='Your work will not be saved if you click Yes. To continue editing and go back to the page, click NO'
          showPrimaryButton
          primaryButtonText='Yes'
          onClickPrimaryButton={onClickPrimaryButton}
          showSecondaryButton
          secondaryButtonText='No'
          onClickSecondaryButton={onClickSecondaryButton}
          footerProps={{size: 'medium', type: 'primary'}}
          isDark
        />
      ) : null}
      {isEditDisplayContent ? (
        <SaveCancelFooter
          FormError={getFormError()}
          handleCancel={() => navigate(returnTo)}
          disableDraft
          draftAction={() => {}}
          nextAction={handleSaveAndPublishEditDisplayContent}
          isReview
        />
      ) : (
        <SaveCancelFooter
          FormError={getFormError()}
          handleCancel={handleCancel}
          draftAction={handleDraftAction}
          nextAction={handleNextActionWrapper}
          disableDraft={disableDraft}
        />
      )}
    </>
  );
};
